import { useRef, useState } from "react";
import useCustomEffect from "./useCustomEffect";

const BUNDLE_PATTERN = /\/static\/js\/main\.[a-z0-9]+\.js/i;
const RELOADED_KEY = "auto_reload_bundle";
const CHECK_INTERVAL = 5 * 60 * 1000;
const MAX_AGE_LIMIT = 24 * 60 * 60 * 1000;

/**
 * This method will return the main bundle path from the given html or the current document
 * @param {string|null} html
 * @returns {string|null}
 */
export function getBundlePath(html = null) {
  if (html === null) {
    const script = document.querySelector('script[src*="/static/js/main."]');
    html = script?.getAttribute("src") ?? "";
  }

  return html.match(BUNDLE_PATTERN)?.[0] ?? null;
}

/**
 * This method will fetch the live index.html, bypassing the browser cache
 * @returns {Promise<string|null>} the html, or null when the server is unreachable
 */
async function fetchIndex() {
  try {
    const response = await fetch(`/index.html?t=${Date.now()}`, {
      cache: "no-store",
    });

    return response.ok ? await response.text() : null;
  } catch (error) {
    return null;
  }
}

/**
 * This method will return the bundle that was last reloaded for
 * @returns {string|null}
 */
function getReloadedBundle() {
  //Locked down kiosk browsers can block the storage
  try {
    return sessionStorage.getItem(RELOADED_KEY);
  } catch (error) {
    return null;
  }
}

/**
 * This method will remember the bundle that is reloaded for
 * @param {string} bundle
 */
function setReloadedBundle(bundle) {
  try {
    sessionStorage.setItem(RELOADED_KEY, bundle);
  } catch (error) {
    //Without storage there is no loop protection, the no-cache header on index.html prevents the loop
  }
}

/**
 * This hook will reload the application while it is idle, when a new release is deployed or when the
 * application has been running longer than the max age (so changes in the backoffice are picked up)
 * @param {boolean} idle whether no one is using the application
 * @param {number} maxAge the time in milliseconds after which the application reloads (at most a day), 0 to disable
 * @param {number} checkInterval the time in milliseconds between the release checks and reload retries
 */
function useAutoReload(idle, maxAge, checkInterval = CHECK_INTERVAL) {
  const currentBundle = useRef(null);
  const loadedAt = useRef(Date.now());
  const idleRef = useRef(idle);
  const reloading = useRef(false);
  const retryTimer = useRef(null);
  const [data, setData] = useState({
    updateAvailable: false,
    expired: false,
    retries: 0,
  });

  idleRef.current = idle;

  /**
   * UseEffect for clearing the retry timer on cleanup
   */
  useCustomEffect(() => {
    return () => clearTimeout(retryTimer.current);
  }, []);

  /**
   * UseEffect for checking periodically if a new release is deployed
   */
  useCustomEffect(() => {
    currentBundle.current = getBundlePath();

    //Development builds have no hashed bundle, so there is nothing to compare
    if (!currentBundle.current || data.updateAvailable) {
      return;
    }

    const timer = setInterval(checkRelease, checkInterval);
    return () => clearInterval(timer);
  }, [data.updateAvailable, checkInterval]);

  /**
   * UseEffect for marking the application as expired once it is older than the max age
   */
  useCustomEffect(() => {
    //Cap at a day, a longer timeout overflows and fires immediately, which would keep reloading
    const age = Math.min(Number(maxAge) || 0, MAX_AGE_LIMIT);
    if (age <= 0 || data.expired) {
      return;
    }

    const timer = setTimeout(
      () => setData((prev) => ({ ...prev, expired: true })),
      Math.max(0, loadedAt.current + age - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [maxAge, data.expired]);

  /**
   * UseEffect for reloading once the application is idle and a reload is required
   */
  useCustomEffect(() => {
    if (idle && (data.updateAvailable || data.expired)) {
      tryReload();
    }
  }, [idle, data.updateAvailable, data.expired, data.retries]);

  /**
   * This method will mark the update as available when the live index.html uses a different bundle
   */
  async function checkRelease() {
    const html = await fetchIndex();
    const bundle = html ? getBundlePath(html) : null;

    //Skip a bundle that was already reloaded for, so a cached index.html can't cause a reload loop
    if (
      bundle &&
      bundle !== currentBundle.current &&
      bundle !== getReloadedBundle()
    ) {
      setData((prev) => ({ ...prev, updateAvailable: true }));
    }
  }

  /**
   * This method will reload the application when the server is reachable, otherwise it retries later
   * so the kiosk doesn't end up on an error page
   */
  async function tryReload() {
    if (reloading.current) {
      return;
    }

    reloading.current = true;
    const html = await fetchIndex();
    reloading.current = false;

    if (html === null) {
      clearTimeout(retryTimer.current);
      retryTimer.current = setTimeout(
        () => setData((prev) => ({ ...prev, retries: prev.retries + 1 })),
        checkInterval,
      );
      return;
    }

    //Someone started using the application during the check
    if (!idleRef.current) {
      return;
    }

    const bundle = getBundlePath(html);
    if (bundle) {
      setReloadedBundle(bundle);
    }

    reloading.current = true;
    window.location.reload();
  }
}

export default useAutoReload;
